import './privacyOpsWidgets.less'
import {
  ACTION_PRIVACY_RIGHTS,
  PrivacyRightsWidget,
  fetchPrivacyRights,
  resetPrivacyRights
} from './privacyOpsWidgetsSlice'
import { RootState } from '../../../../../rootReducer'
import { getShowLoader } from '../../../../../reducers/requestReducer'
import LoaderView from '../../../../../components/Loader/loader'
import historyService from '../../../../../services/historyService'
import {
  URL_CONSENT_MANAGEMENT,
  URL_COOKIES_CONSENT,
  URL_DOMAINS,
  URL_DSR,
  URL_DSR_REQUEST_MANAGEMENT,
  URL_OVERVIEW
} from '../../../../../constants'
import { FeatureFlags } from '../../../../../configs/featureFlagSlice'
import { mockPrivacyRightsWidgetData } from '../../../renderer/utils/widgetMockdata'
import { connect } from 'react-redux'
import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { Typography } from '@lightbeamai/design-system'

interface OwnProps {
  useMockData?: boolean
}

interface StateProps {
  privacyRights?: PrivacyRightsWidget
  showLoader?: boolean
  featureFlags: FeatureFlags
}

interface DispatchProps {
  fetchPrivacyRights: () => void
  resetPrivacyRights: () => void
}

type PrivacyRightsComponentProps = StateProps & DispatchProps & OwnProps

const PrivacyRightsWidget = (props: PrivacyRightsComponentProps) => {
  const {
    privacyRights,
    showLoader = false,
    useMockData = false,
    fetchPrivacyRights,
    resetPrivacyRights,
    featureFlags
  } = props

  const {
    dsrOpen = 0,
    dsrTotal = 0,
    cookieConsentTotal = 0,
    cookieConsentDomainsTotal = 0,
    consentManagementOptIns = 0,
    consentManagementOptOuts = 0,
    consentManagementTotalConsents = 0
  } = privacyRights || {}

  const intl = useIntl()

  useEffect(() => {
    if (useMockData) return
    fetchPrivacyRights()
    return () => resetPrivacyRights()
  }, [fetchPrivacyRights, resetPrivacyRights, useMockData])

  // dsr
  const dsrPercentage = Math.round((dsrOpen / dsrTotal) * 100) || 0
  const handleOpenDsr = () => historyService.push(URL_DSR + URL_DSR_REQUEST_MANAGEMENT)

  // consent management
  const consentManagementPercentage =
    Math.round((consentManagementOptIns / consentManagementTotalConsents) * 100) || 0
  const handleOpenConsentManagement = () =>
    historyService.push(URL_CONSENT_MANAGEMENT + URL_OVERVIEW)

  // cookies consent
  const handleOpenCookiesConsent = () => historyService.push(URL_COOKIES_CONSENT + URL_DOMAINS)

  return (
    <div className="lb-widget-card policy-rights xs-p-16">
      <div className="privacy-ops-widget" data-test-id="privacy-rights-widget">
        <Typography color="primary" variant="h6">
          {intl.formatMessage({ id: 'privacyOpsWidget.privacyRights' })}
        </Typography>
        <div className="widget-bars">
          <div className="widget-section clickable" onClick={handleOpenDsr}>
            <Typography
              color="primary"
              className="xs-mb-0 title"
              type="medium"
              variant="baseMinus2"
            >
              {intl.formatMessage({ id: 'privacyOpsWidget.dsr' })}
            </Typography>
            <div className="progress-bar">
              <div className="progress" style={{ width: dsrPercentage + '%' }} />
            </div>
            <div className="counts row" title={dsrOpen + ''}>
              <div className="divider" />
              <div className="count-box row">
                <Typography color="primary" className="xs-mb-0" type="strong" variant="baseMinus2">
                  {intl.formatNumber(dsrOpen, { notation: 'compact' })}
                </Typography>
                <Typography
                  color="secondary"
                  className="xs-ml-4 xs-mb-0"
                  type="normal"
                  variant="baseMinus2"
                >
                  {intl.formatMessage({ id: 'open' })}
                </Typography>
              </div>
              <div className="divider" />
              <div className="count-box row" title={dsrTotal + ''}>
                <Typography color="primary" className="xs-mb-0" type="strong" variant="baseMinus2">
                  {intl.formatNumber(dsrTotal, { notation: 'compact' })}
                </Typography>
                <Typography
                  color="secondary"
                  className="xs-ml-4 xs-mb-0"
                  type="normal"
                  variant="baseMinus2"
                >
                  {intl.formatMessage({ id: 'total' })}
                </Typography>
              </div>
            </div>
          </div>
          {featureFlags.consentManagement && (
            <div className="widget-section clickable" onClick={handleOpenConsentManagement}>
              <div className="section-title">
                <Typography color="primary" className="xs-mb-0" type="medium" variant="baseMinus2">
                  {intl.formatMessage({ id: 'privacyOpsWidget.consentManagement' })}
                </Typography>
              </div>
              <div className="progress-bar">
                <div className="progress" style={{ width: consentManagementPercentage + '%' }} />
              </div>
              <div className="counts row" title={consentManagementOptIns + ''}>
                <div className="divider" />
                <div className="count-box row">
                  <Typography
                    color="primary"
                    className="xs-mb-0"
                    type="strong"
                    variant="baseMinus2"
                  >
                    {intl.formatNumber(consentManagementOptIns, { notation: 'compact' })}
                  </Typography>
                  <Typography
                    color="secondary"
                    className="xs-ml-4 xs-mb-0"
                    type="normal"
                    variant="baseMinus2"
                  >
                    {intl.formatMessage({ id: 'privacyOpsWidget.optIns' })}
                  </Typography>
                </div>
                <div className="divider" />
                <div className="count-box row" title={consentManagementOptOuts + ''}>
                  <Typography
                    color="primary"
                    className="xs-mb-0"
                    type="strong"
                    variant="baseMinus2"
                  >
                    {intl.formatNumber(consentManagementOptOuts, { notation: 'compact' })}
                  </Typography>
                  <Typography
                    color="secondary"
                    className="xs-ml-4 xs-mb-0"
                    type="normal"
                    variant="baseMinus2"
                  >
                    {intl.formatMessage({ id: 'privacyOpsWidget.optOuts' })}
                  </Typography>
                </div>
                <div className="divider" />
                <div className="count-box row" title={consentManagementTotalConsents + ''}>
                  <Typography
                    color="primary"
                    className="xs-mb-0"
                    type="strong"
                    variant="baseMinus2"
                  >
                    {intl.formatNumber(consentManagementTotalConsents, {
                      notation: 'compact'
                    })}
                  </Typography>
                  <Typography
                    color="secondary"
                    className="xs-ml-4 xs-mb-0"
                    type="normal"
                    variant="baseMinus2"
                  >
                    {intl.formatMessage({ id: 'total' })}
                  </Typography>
                </div>
              </div>
            </div>
          )}
          <div className="widget-section clickable" onClick={handleOpenCookiesConsent}>
            <div className="section-title">
              <Typography
                color="primary"
                className="xs-mb-0 title"
                type="medium"
                variant="baseMinus2"
              >
                {intl.formatMessage({ id: 'privacyOpsWidget.cookieConsent' })}
              </Typography>
            </div>
            <div className="counts row" title={cookieConsentDomainsTotal + ''}>
              <div className="divider" />
              <div className="count-box row">
                <Typography color="primary" className="xs-mb-0" type="strong" variant="baseMinus2">
                  {intl.formatNumber(cookieConsentDomainsTotal, { notation: 'compact' })}
                </Typography>
                <Typography
                  color="secondary"
                  className="xs-ml-4 xs-mb-0"
                  type="normal"
                  variant="baseMinus2"
                >
                  {intl.formatMessage({ id: 'privacyOpsWidget.totalDomains' })}
                </Typography>
              </div>
              <div className="divider" />
              <div className="count-box row" title={cookieConsentTotal + ''}>
                <Typography color="primary" className="xs-mb-0" type="strong" variant="baseMinus2">
                  {intl.formatNumber(cookieConsentTotal, { notation: 'compact' })}
                </Typography>
                <Typography
                  color="secondary"
                  className="xs-ml-4 xs-mb-0"
                  type="normal"
                  variant="baseMinus2"
                >
                  {intl.formatMessage({ id: 'cookieConsent.domains.totalCookies' })}
                </Typography>
              </div>
            </div>
          </div>
        </div>

        <LoaderView showLoader={showLoader} />
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  privacyRights: ownProps?.useMockData
    ? mockPrivacyRightsWidgetData
    : state.privacyOpsWidgets.privacyRights,
  showLoader: getShowLoader(ACTION_PRIVACY_RIGHTS),
  featureFlags: state.featureFlags
})

const mapDispatchToProps: DispatchProps = {
  fetchPrivacyRights,
  resetPrivacyRights
}

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(PrivacyRightsWidget)
